import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, CalendarPlus, StickyNote, ListChecks, Megaphone, Star, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useModules } from "@/context/ModulesContext";

/**
 * Persistent "+ New" dropdown surfacing every create action in the app.
 * Placed in the sidebar rail so it's reachable from every screen — turns
 * creation into a 2-click action (open menu → pick action).
 */
export default function NewMenu() {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();
  const { modules } = useModules();
  const canManage = user.role === "org_admin" || user.role === "production_staff";
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);
  if (!canManage) return null;

  const items = [
    { id: "rehearsal", label: "New rehearsal", icon: CalendarPlus, hint: "Add a call to the calendar", go: () => nav("/calendar?new=1") },
    { id: "note", label: "New production note", icon: StickyNote, hint: "Post a note (tag cast to share)", go: () => nav("/notes-hub?tab=production&new=1") },
    { id: "cast-note", label: "New cast note", icon: StickyNote, hint: "Send a note to specific cast", go: () => nav("/notes-hub?tab=cast&new=1") },
    (modules?.rehearsal_reports !== false)
      ? { id: "report", label: "New rehearsal report", icon: ListChecks, hint: "File after a rehearsal", go: () => nav("/rehearsals?tab=reports&new=1") }
      : null,
    { id: "announcement", label: "New announcement", icon: Megaphone, hint: "Broadcast to the company", go: () => nav("/announcements?new=1") },
    { id: "audition", label: "New audition slot", icon: Star, hint: "Open more audition times", go: () => nav("/auditions?slots=1") },
  ].filter(Boolean);

  return (
    <div className="relative" data-testid="new-menu">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="new-menu-trigger"
        style={{ backgroundColor: "#F59E0B", color: "#0A0A0A" }}
        className="w-full h-9 rounded-md font-semibold text-sm inline-flex items-center justify-between px-3 hover:brightness-110 transition-all"
      >
        <span className="inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> New</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 left-0 right-0 rounded-md border border-white/10 bg-[#0F0F0F] shadow-[0_15px_40px_-10px_rgba(0,0,0,0.7)] p-1 min-w-[240px]" data-testid="new-menu-panel">
            {items.map((it) => (
              <button
                key={it.id}
                data-testid={`new-menu-${it.id}`}
                onClick={() => { setOpen(false); it.go(); }}
                className="w-full text-left px-2.5 py-2 rounded hover:bg-gold/10 hover:text-gold transition-colors flex items-start gap-2.5 group"
              >
                <it.icon className="w-3.5 h-3.5 mt-0.5 text-zinc-500 group-hover:text-gold shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 group-hover:text-gold leading-tight">{it.label}</div>
                  <div className="text-[10px] text-zinc-500 leading-tight mt-0.5">{it.hint}</div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
